/**
 * Reusable SVG flyout used by the chart-axis hover tooltip (FL-209).
 *
 * Renders a styled rect + multi-line text inside an SVG `<g>`. Intended to be
 * placed inside an SVG context (e.g. inside a Victory chart). Position is
 * clamped to a minimum of 8px from the parent SVG's origin so the flyout
 * doesn't extend past the chart's left/top edge.
 */
import React from "react";
import { tooltipFlyoutWidth } from "./labelHelpers";

export interface HoverFlyoutProps {
  /** Origin x of the labelled element (e.g. axis tick position). */
  x: number;
  /** Origin y of the labelled element. */
  y: number;
  /** Wrapped label lines (one per visual line). */
  lines: string[];
  /** Pixel height of one line in the flyout. Default 14. */
  lineHeight?: number;
}

export const FLYOUT_LINE_HEIGHT = 14;
export const FLYOUT_VERTICAL_PADDING = 16;
export const FLYOUT_GAP_FROM_LABEL = 8;
export const FLYOUT_MIN_INSET = 8;

/** Compute height for a flyout with `lineCount` lines (used by tests too). */
export function flyoutHeight(
  lineCount: number,
  lineHeight = FLYOUT_LINE_HEIGHT,
): number {
  return lineCount * lineHeight + FLYOUT_VERTICAL_PADDING;
}

export const HoverFlyout: React.FC<HoverFlyoutProps> = ({
  x,
  y,
  lines,
  lineHeight = FLYOUT_LINE_HEIGHT,
}) => {
  const flyoutW = tooltipFlyoutWidth(lines);
  const flyoutH = flyoutHeight(lines.length, lineHeight);
  const tx = Math.max(FLYOUT_MIN_INSET, x - flyoutW / 2);
  const ty = Math.max(FLYOUT_MIN_INSET, y - flyoutH - FLYOUT_GAP_FROM_LABEL);
  return (
    <g
      transform={`translate(${tx}, ${ty})`}
      pointerEvents="none"
      style={{ filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.4))" }}
      data-testid="hover-flyout"
    >
      <rect
        width={flyoutW}
        height={flyoutH}
        fill="#FCFCFC"
        stroke="#252525"
        strokeWidth={1}
        rx={5}
      />
      <text
        x={flyoutW / 2}
        y={lineHeight + 2}
        textAnchor="middle"
        fill="#252525"
        fontSize={11}
      >
        {lines.map((line, i) => (
          <tspan key={i} x={flyoutW / 2} dy={i === 0 ? 0 : lineHeight}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
};
